/**
 * IMPORTANT:
 *
 * This file is used to define the types for the FSS bridge.
 * It is used to ensure that the types are consistent between the FSS and the client.
 *
 * IF YOU NEED TO MAKE A CHANGE TO THE TYPES, YOU MUST HAVE A CORRESPONDING PR TO MAKE
 * CHANGES TO THE FSS SIDE AT THE SAME TIME.
 *
 * `metaserver/static/js/dropbox_ai_assistant/dash_chat/constants.ts`
 */

/**
 * Conversation source constants - string enum for type safety and runtime access
 */
export enum ConversationSource {
  FssUnscoped = "fss-unscoped",
  FssFile = "fss-file",
  FssFolder = "fss-folder",
  FssSerpQuery = "fss-serp-query",
  FssSerpTypeahead = "fss-serp-typeahead",
}

/**
 * Array of all conversation source values for runtime iteration
 */
export const CONVERSATION_SOURCES = Object.values(ConversationSource);

export type ActionSource = DashActionEvent["source"] | FSSActionEvent["source"];

/**
 * The fields required to show a basic representation of an FSS entity in Dash UI.
 */
export type FSSEntity = FSSFile | FSSFolder;

export type FSSFile = {
  type: "file";
  fileId: string;
  name: string;
  href: string | undefined;
};

export type FSSFolder = {
  type: "folder";
  fileId: string;
  path: string;
};

type ConversationEventBase = {
  eventType: "conversation";
  source: ConversationSource;
  data: unknown;
  changeScope?: boolean;
  agentIds?: string[];
  scenario?: {
    type: string;
    id: string;
  };
};

type OpenUnscopedSidebarEvent = ConversationEventBase & {
  source: ConversationSource.FssUnscoped;
  data: {
    createNewSession?: boolean;
    searchFss: boolean;
  };
};

type SendFileMessage = ConversationEventBase & {
  source: ConversationSource.FssFile;
  data: {
    createNewSession?: boolean;
    agentIds?: string[];
    files: FSSFile[];
  };
};

type SendFolderMessage = ConversationEventBase & {
  source: ConversationSource.FssFolder;
  data: {
    createNewSession?: boolean;
    agentIds?: string[];
    folders: FSSFolder[];
  };
};

type SendSearchQueryMessage = ConversationEventBase & {
  source: ConversationSource.FssSerpQuery;
  data: {
    searchQuery: string;
    createNewSession?: boolean;
    searchFss?: boolean;
  };
};

type SendSearchTypeaheadMessage = ConversationEventBase & {
  source: ConversationSource.FssSerpTypeahead;
  data: {
    searchQuery: string;
    createNewSession?: boolean;
    searchFss?: boolean;
    files?: FSSFile[];
  };
};

export type UnavailableSource = {
  source: FSSEntity;
  localizedMessage: string;
};

export type ConversationEvent =
  | OpenUnscopedSidebarEvent
  | SendFileMessage
  | SendFolderMessage
  | SendSearchQueryMessage
  | SendSearchTypeaheadMessage;

/**
 * type for Actions that can be sent or received from the FSS side
 */
type ActionEventBase = {
  eventType: "action";
  source: string;
  data?: unknown;
  scenario?: {
    type: string;
    id: string;
  };
};

type ReadyPageEvent = ActionEventBase & {
  source: "ready.page";
  data?: undefined;
};

type UpdateThemeEvent = ActionEventBase & {
  source: "update.theme";
  data: {
    darkSetting: string; // should match DarkSetting type in Dash Chat
  };
};

type OpenSourcePickerEvent = ActionEventBase & {
  source: "open.source-picker";
  data?: undefined;
};

type AddSourceEvent = ActionEventBase & {
  source: "add.source";
  data: {
    sources: FSSEntity[];
  };
};

type CloseSidebarEvent = ActionEventBase & {
  source: "close.sidebar";
  data?: undefined;
};

type OpenSessionHistoryEvent = ActionEventBase & {
  source: "open.session-history";
  data?: undefined;
};

type NotifyLoginEvent = ActionEventBase & {
  source: "notify.login";
  data: {
    userId: number;
  };
};

type NotifyLogoutEvent = ActionEventBase & {
  source: "notify.logout";
  data?: undefined;
};

type UpdateConversationEvent = ActionEventBase & {
  source: "update.conversation";
  data: {
    conversationId: string;
  };
};

type OpenUnavailableSourcesEvent = ActionEventBase & {
  source: "open.unavailable-sources";
  data: {
    unavailableSources: UnavailableSource[];
  };
};

/**
 * Action events that can be sent from Dash to FSS
 */
type DashActionEvent =
  | ReadyPageEvent
  | OpenSourcePickerEvent
  | OpenSessionHistoryEvent
  | CloseSidebarEvent
  | NotifyLoginEvent
  | NotifyLogoutEvent
  | UpdateConversationEvent
  | OpenUnavailableSourcesEvent;

/**
 * Action events that can be sent from FSS to Dash
 */
export type FSSActionEvent =
  | AddSourceEvent
  | UpdateConversationEvent
  | UpdateThemeEvent;

/**
 * Messages that can be sent from FSS to Dash
 */
export type FSSMessageEvent = ConversationEvent | FSSActionEvent;

/**
 * Messages that can be sent from Dash to FSS
 */
export type DashMessageEvent = DashActionEvent;
